import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllInsepctionTracks } from "@/services/houseInspections";
import { useTableFilter } from "@/utils/hooks/useFilter";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { Col, Row } from "reactstrap";
import { LabelValue } from "@/Component/Value";
import { ArrivalCard } from "../Makkah/Reception/ArrivalCard";
import { DATE_TIME_FORMAT_12_HOURS, EXAMINATION_TYPES, ORDER_STATUS, permissions } from "@/utils/constants";
import { useNavigate } from "react-router";
import { CountModal } from "@/Component/CountModal";
import { Checkbox, Flex, Tag } from "antd";
import { Building06Icon, NoteIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Stack } from "react-bootstrap";
import { useHousingInspectionContext } from "../Makkah/HousingAndAccommodations/HousingInspectionContext/housingContext";
import { SelectStickyAction } from "@/Component/SelectStickyAction";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useSystem } from "@/utils/hooks/useLocation";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { Can } from "@/CommonElements/PermissionGate";

export const HousingInspectionNotes = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { confirm, ModalComponent } = useConfirm();
  const { getLocalizedText, getLocalizedDate } =
    useLocalizedText();
  const { currentSystem, city_id,isMakkah } = useSystem();
  const navigate = useNavigate();
  const { filters, filterFields: filterConfig } = useTableFilter({});
  const { t } = useTranslation();
  const { debouncedValue } = useDebouncedSearch(
    "",
    500
  );
  const [openCreateOrderModal, setOpenCreateOrderModal] = useState(false);
  const {
    setInspectionState,
    inspectionState: { selectedInspections, type },
    removeSelectedInspection,
    clearSelectedInspections,
  } = useHousingInspectionContext();
  // ===============================
  // Queries
  // ===============================

  const {
    data: InspectionTracksRes,
    isLoading,
  } = useAllInsepctionTracks({
    ...filters,
    page: currentPage,
    per_page: 10,
    search: debouncedValue,
    city_id,
  });

  const InspectionData = isLoading
    ? Array.from({ length: 10 }, (_, index) => ({ id: index, loading: true }))
    : InspectionTracksRes?.data?.data ?? [];
  const meta = InspectionTracksRes?.data?.meta;

  const labelValues = [
    {
      key: "houseCommercialNameAr",
      label: t("BUILDING_NAME"),
      render: (value, rowData) => (
        <LabelValue.Value
          value={rowData?.house?.houseCommercialNameAr || "-"}
        />
      ),
    },
    {
      key: "OwnerName",
      label: t("HOTEL_MANAGER_NAME"),
      render: (value, rowData) => (
        <LabelValue.Value value={rowData?.house?.OwnerName || "-"} />
      ),
    },
    {
      key: "REP_MOBILE_NO",
      label: t("REP_MOBILE_NO"),
      render: (value, rowData) => (
        <LabelValue.Value value={rowData?.house?.REP_MOBILE_NO || "-"} />
      ),
    },
    {
      key: "HOUSE_ADDRESS_1",
      label: t("ADDRESS"),
      render: (value, rowData) => (
        <LabelValue.Value
          value={
            rowData?.house?.HOUSE_ADDRESS_1 ||
            [
              rowData?.house?.houseCommercialNameAr,
              rowData?.house?.cityName,
            ].join(" - ") ||
            "-"
          }
        />
      ),
    },
    {
      key: "reform_deadline",
      label: t("مهلة الاصلاح"),
      render: (value) => (
        <LabelValue.Value value={[value, t("HOURE")].join(" ") || "-"} />
      ),
    },
    {
      key: "created_at",
      label: t("وقت انشاء التتبع"),
      render: (value) => (
        <LabelValue.Value
          value={
            value ? getLocalizedDate(value, DATE_TIME_FORMAT_12_HOURS) : "-"
          }
        />
      ),
    },
    {
      key: "items",
      label: t("ITEMS_COUNT"),
      render: (value) => (
        <CountModal
          modal={{
            props: {
              size: "lg",
              centered: true,
              scrollable: true,
            },
            header: {
              children: t("ITEMS_COUNT"),
            },
            footer: {},
            body: {
              children: (
                <ul className="p-0 d-flex flex-column gap-2">
                  {value.map((item) => (
                    <li key={item.id} className="border py-2 px-1 rounded-2">
                      <Row>
                        <Col xs={10}>
                          <LabelValue.Value
                            value={getLocalizedText(
                              item.item_name_ar,
                              item.item_name_en
                            )}
                          />
                          <div className="mt-2">
                            <LabelValue
                              direction="vertical"
                              label={
                                <Stack direction="horizontal" gap={2}>
                                  <HugeiconsIcon size={16} icon={NoteIcon} />
                                  <span>{t("NOTES")}</span>
                                </Stack>
                              }
                              value={item.notes || "-"}
                            />
                          </div>
                        </Col>
                        <Col xs={2} style={{ position: "relative" }}>
                          <Tag
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "-2px",
                            }}
                            color="red"
                          >
                            {t("NOT_AVAILABLE")}
                          </Tag>
                        </Col>
                      </Row>
                    </li>
                  ))}
                </ul>
              ),
            },
            props: {},
          }}
        >
          {value.length}
        </CountModal>
      ),
    },
  ];

  const getStatusConfig = (item) => {
    const statusMap = {
      not_created: {
        color: "danger",
        text: getLocalizedText(item?.status?.label_ar, item?.status?.label_en),
      },
      order_created: {
        color: "success",
        text: getLocalizedText(item?.status?.label_ar, item?.status?.label_en),
      },
    };

    return statusMap[item?.status?.value] || statusMap.not_created;
  };

  const noOrdersFound = !isLoading && InspectionData.length === 0;

  const handleCheckboxChange = (item, checked) => {
    if (checked) {
      setInspectionState((prev) => ({
        ...prev,
        selectedInspections: [...(prev?.selectedInspections || []), item],
        type: EXAMINATION_TYPES.track,
      }));
    } else {
      removeSelectedInspection(item);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="relative">
      <Row>
        {noOrdersFound && <NoOrdersFound />}
        {InspectionData.map((item) => {
          return (
            <ArrivalCard
              loading={item.loading}
              key={item.id}
              actions={undefined}
              statusConfig={getStatusConfig(item)}
              title={
                <Flex gap={8}>
                  {item?.status?.value === ORDER_STATUS.not_created && (
                    <Can
                      permissions={[
                        isMakkah
                          ? permissions.create_house_inspection_track_order
                          : permissions.create_house_inspection_track_order_for_madina
                      ]}
                    >
                      <Checkbox
                        checked={selectedInspections?.some((inspection) => inspection.house.id === item.house.id)}
                        className="square-checkbox"
                        onChange={(val) => {
                          if (!!selectedInspections.length && type !== EXAMINATION_TYPES.track) {
                            confirm({
                              title: t("CONFIRM"),
                              content: t("CONFIRM_CLEAR_INSPECTION_ORDERS"),
                              okText: t("CONFIRM"),
                              cancelText: t("CANCEL"),
                              onOk: () => {
                                clearSelectedInspections();
                                handleCheckboxChange(item, val.target.checked);
                              },
                            });

                            return;
                          }

                          handleCheckboxChange(item, val.target.checked);
                        }}
                      />
                    </Can>
                  )}
                  <span>{t("PERMIT_NUMBER") + ": "}</span>
                </Flex>
              }
              titleValue={<LabelValue.Value value={item?.house?.permit_no || "-"} />}
              arrival={item}
              labelValues={labelValues}
            />
          );
        })}
      </Row>
      <ModalComponent />

      <Can
        permissions={[
          isMakkah
            ? permissions.create_house_inspection_track_order
            : permissions.create_house_inspection_track_order_for_madina
        ]}
      >
        <div className="relative">
          {type === "track" && (
            <SelectStickyAction
              modalProps={{
                showClearSelectionConfirm: openCreateOrderModal,
                setShowClearSelectionConfirm: () => setOpenCreateOrderModal(false),
                handleConfirmClearSelection: () => clearSelectedInspections(),
              }}
              okButtonText={t("CREATE_TRACK_ORDER")}
              selectedCount={selectedInspections.length}
              handleClearSelectionClick={() => setOpenCreateOrderModal(true)}
              handleSubmitSelected={() => {
                navigate(`/app/${currentSystem}/housing-inspection-orders/create`, {
                  state: {
                    selectedInspections,
                  },
                });
              }}
            />
          )}
        </div>
      </Can>

      <AntPagination
        className="sticky-bottom z-1 w-100 p-3 bg-white"
        hideOnSinglePage
        pageSize={meta?.per_page || 10}
        current={currentPage}
        onChange={handlePageChange}
        total={meta?.total || 0}
        showSizeChanger={false}
      />
    </div>
  );
};

const NoOrdersFound = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center py-5">
      <HugeiconsIcon icon={Building06Icon} size={48} className="text-muted" />
      <h4 className="text-muted mb-2">{t("NO_NOTES_FOUND")}</h4>
    </div>
  );
};
